import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
  TextField,
  Modal,
} from "@mui/material";
import { Close } from "@material-ui/icons";
import Backdrop from "@material-ui/core/Backdrop";
import { useStyles } from "./styles";
import { useState } from "react";

/**
 * ConfirmDialog component displays a dialog with a title, description, and action buttons.
 *
 * @param {Object} props - The props for the ConfirmDialog component.
 * @param {boolean} props.open - Determines whether the dialog is open.
 * @param {string} props.actionTitle - The title of the dialog.
 * @param {string} props.actionDescription - The description or message to display in the dialog.
 * @param {Function} [props.onConfirm] - The function to handle the confirmation action.
 * @param {Function} [props.onCancel] - The function to handle the cancellation action.
 * @param {string} props.inputLabel - The Label for input.
 * @param {string} props.inputValue - The current value of an input.
 * @param {Function} [props.setInputValue] - The Function that updates the input values with inputField's onChange event.
 * @param {string} [props.type] - The type of dialog ("confirm" or "input").
 * @returns {JSX.Element} The ConfirmDialog component.
 */
const ConfirmDialog = ({
  open,
  actionTitle,
  actionDescription,
  onConfirm = () => {},
  onCancel = () => {},
  inputLabel,
  inputValue = "",
  inputRequired,
  maxLength,
  setInputValue = () => {},
  type = "confirm",
}) => {
  const classes = useStyles();
  const [helperText, setHelperText] = useState();
  const [blur, setBlur] = useState(false);


  const validInput = (value) => {
    if(inputRequired && !value.trim()) {
      setHelperText(`${inputLabel} is required.`);
      return false;
    }
    if(maxLength && value.trim().length > maxLength) {
      setHelperText(`${inputLabel} exceeds ${maxLength} characters`);
      return false;
    }
    return true;
  }

  const handleClose = () => {
    setBlur(false);
    onCancel();
  }

  if (!actionTitle) return <></>;
  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      className={classes.modal}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Dialog onClose={handleClose} open={true} maxWidth="xs">
        <DialogTitle className={classes.dialogHeading} sx={{ pb: 1 }}>
          <Typography variant="h6">{actionTitle}</Typography>
        </DialogTitle>

        <Box className={classes.closeIconWrapper} onClick={handleClose}>
          <IconButton size="small">
            <Close className={classes.dialogCloseIcon}/>
          </IconButton>
        </Box>

        <DialogContent sx={{ py: 2 }}>
          {(type === "confirm" || type === "info") && (
            <Typography variant="body1" sx={{ fontSize: '0.95rem' }}>{actionDescription}</Typography>
          )}

          {type === "input" && (
            <TextField
              maxRows={12}
              type="text"
              label={inputLabel}
              value={inputValue}
              onChange={(e) => {
                setBlur(validInput(e.target.value) ? false : true)
                setInputValue(e);
              }}
              onBlur={(e) => {
                setBlur(validInput(e.target.value) ? false : true);
              }}
              variant="outlined"
              fullWidth
              sx={{ minWidth: "93%" }}
              autoFocus
              required={inputRequired}
              error={blur && !!helperText}
              helperText={blur ?  helperText : null}
            />
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          {type === "info" ? (
            <Button variant="contained" size="small" onClick={onConfirm}>
              OK
            </Button>
          ) : (
            <>
              <Button variant="contained" color="error" size="small" onClick={handleClose}>
                Cancel
              </Button>
              <Button variant="contained" size="small" onClick={() => {
                if(validInput(inputValue)) {
                  onConfirm();
                  setBlur(false);
                } else {
                  setBlur(true);
                }
              }}>
                Confirm
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Modal>
  );
};

export default ConfirmDialog;